import * as aes from '../../lib/aes';

/**
 * Decrypts an encrypted file blob using AES-CBC with keys from WebAssembly module.
 * This is the standard decryption for files stored on the server.
 *
 * @param blob - The encrypted blob to decrypt
 * @param mimeType - Optional MIME type for the decrypted blob (default: 'application/octet-stream')
 * @returns Object containing the decrypted blob and the original encrypted bytes
 */
export async function decryptBlob(
  blob: Blob,
  mimeType: string = 'application/octet-stream',
): Promise<{ decrypted: Blob; encryptedBytes: Uint8Array }> {
  // WebAssembly crypto module is hydrated from environment
  const wasmBytesString = import.meta.env.VITE_APP_WASM_MODULE_BYTES as string;
  const wasmBytes = wasmBytesString.split(',').map(Number);

  // Spin up WASM instance and get buffers/keys
  const { instance } = await WebAssembly.instantiate(new Uint8Array(wasmBytes));
  const memory = new Uint8Array((instance.exports as any).memory.buffer);
  const _x = (instance.exports as any).x();
  const _y = (instance.exports as any).y();
  const x = memory.subarray(_x, _x + 32);
  const y = memory.subarray(_y, _y + 16);
  const aesCbc = new aes.ModeOfOperation.cbc(x, y);

  // Transform blob to Uint8Array and decrypt
  const fileArrayBuffer = await blob.arrayBuffer();
  const encryptedBytes = new Uint8Array(fileArrayBuffer);
  const bytesToRemove = encryptedBytes[encryptedBytes.length - 1]!;

  // Remove padding indicator byte and decrypt
  const decryptedBytes = aesCbc.decrypt(encryptedBytes.slice(0, -1));

  // Remove PKCS padding for final blob
  const decrypted = new Blob([decryptedBytes.slice(0, bytesToRemove * -1)], {
    type: mimeType,
  });

  return { decrypted, encryptedBytes };
}

/**
 * Decrypts a blob and returns the content as text (UTF-8).
 * Useful for JSON files like .fgsecret.
 *
 * @param blob - The encrypted blob to decrypt
 * @returns Object containing the decrypted text and the original encrypted bytes
 */
export async function decryptBlobAsText(
  blob: Blob,
): Promise<{ decryptedText: string; decrypted: Blob; encryptedBytes: Uint8Array }> {
  // WebAssembly crypto module is hydrated from environment
  const wasmBytesString = import.meta.env.VITE_APP_WASM_MODULE_BYTES as string;
  const wasmBytes = wasmBytesString.split(',').map(Number);

  // Spin up WASM instance and get buffers/keys
  const { instance } = await WebAssembly.instantiate(new Uint8Array(wasmBytes));
  const memory = new Uint8Array((instance.exports as any).memory.buffer);
  const _x = (instance.exports as any).x();
  const _y = (instance.exports as any).y();
  const x = memory.subarray(_x, _x + 32);
  const y = memory.subarray(_y, _y + 16);
  const aesCbc = new aes.ModeOfOperation.cbc(x, y);

  // Transform blob to Uint8Array and decrypt
  const fileArrayBuffer = await blob.arrayBuffer();
  const encryptedBytes = new Uint8Array(fileArrayBuffer);
  const bytesToRemove = encryptedBytes[encryptedBytes.length - 1]!;

  // Remove padding indicator byte and decrypt
  const decryptedBytes = aesCbc.decrypt(encryptedBytes.slice(0, -1));

  // Remove PKCS padding
  const finalBytes = decryptedBytes.slice(0, bytesToRemove * -1);

  // Convert bytes to text using TextDecoder
  const decoder = new TextDecoder('utf-8');
  const decryptedText = decoder.decode(finalBytes);

  // Create blob from text content
  const decrypted = new Blob([decryptedText], {
    type: 'application/json',
  });

  return { decryptedText, decrypted, encryptedBytes };
}
